import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageView } from './usePageView';
import { usePageDuration } from './usePageDuration';
import { trackEvent } from './analyticsService';
import { getPageTitle } from '@configs/routes';
import type { EventType } from './types';

interface AnalyticsProviderProps {
  children: ReactNode;
  userId?: string;
}

/**
 * 전역 Analytics Provider
 * - 페이지 조회/체류 시간 자동 추적
 * - data-track 속성이 있는 요소 클릭 시 자동 추적
 *
 * @example
 * // App.tsx 또는 CommonLayout에서
 * <AnalyticsProvider userId={user?.id}>
 *   {children}
 * </AnalyticsProvider>
 *
 * // 버튼에 data-track 속성 추가
 * <button data-track="다운로드_버튼">다운로드</button>
 * <a href="/products" data-track="분석 스토어_링크">마켓</a>
 *
 * // 이벤트 타입 지정 (기본: click)
 * <button data-track="폼_제출" data-track-type="submit">제출</button>
 */
export const AnalyticsProvider = ({ children, userId }: AnalyticsProviderProps) => {
  const { pathname } = useLocation();

  // 페이지 조회 추적
  usePageView({ userId });

  // 페이지 체류 시간 추적
  usePageDuration({ userId });

  // data-track 속성 클릭 이벤트 자동 추적
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const trackElement = target.closest('[data-track]') as HTMLElement | null;

      if (trackElement) {
        const eventName = trackElement.dataset.track;
        const eventType = (trackElement.dataset.trackType as EventType) || 'click';
        // data-track-page 속성이 있으면 해당 값 사용, 없으면 현재 pathname 사용
        const customPageUrl = trackElement.dataset.trackPage;
        const pageUrl = customPageUrl || pathname;
        const pageName = customPageUrl || getPageTitle(pathname) || pathname;

        if (eventName) {
          trackEvent(
            {
              eventType,
              eventName,
              pageUrl,
              pageName,
              metadata: {
                elementTag: trackElement.tagName.toLowerCase(),
                elementText: trackElement.textContent?.trim().slice(0, 50),
              },
            },
            userId,
          ).catch((error) => {
            console.error('[Analytics] Event tracking failed:', error);
          });
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [pathname, userId]);

  return <>{children}</>;
};
